import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import { Navigate, RouterProvider, ScrollRestoration, createBrowserRouter } from 'react-router-dom';
import './main.css';
import { UserFeatureFlag } from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { AutomaticModalsComponent } from '@components/shared/automatic-modals.component.tsx';
import { ImpersonationBanner } from '@components/shared/impersonation-banner.component.tsx';
import { LoadingStateContainer } from '@components/shared/loading-state-container.component.tsx';
import { Toaster } from '@components/ui/toaster.tsx';
import { queryClient } from '@queries/client.ts';
import { queryKeys } from '@queries/keys.ts';
import { invitationStore, setInviteTokenSelector } from '@stores/invitation.store';
import { useLocalSessionStore, useLocalSessionSync } from '@stores/local-session.store.ts';
import { referralStore, setReferrerCodeSelector } from '@stores/referral.store';
import { clearAppCache } from '@utils/auth.util.ts';
import { shouldRedirectAuthenticatedUserToAdmin } from '@utils/impersonation-session.util.ts';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { useEffect } from 'react';
import { useStore } from 'zustand';
import { InsightsIframeManager } from './components/shared/insights-iframe-manager.component.tsx';
import { ThreadsIframeManager } from './components/shared/threads-iframe-manager.component.tsx';

const LoadingState = () => {
  return (
    <div className="flex justify-center items-center h-[calc(100svh-var(--impersonation-banner-height,0px))]">
      <LoadingStateContainer message={null} />
    </div>
  );
};

export const App = () => {
  const setInviteToken = useStore(invitationStore, setInviteTokenSelector);
  const setReferrerCode = useStore(referralStore, setReferrerCodeSelector);
  const { hasLocalSession } = useLocalSessionStore();
  useLocalSessionSync();
  const { data: user, isPending: isUserPending } = useGetCurrentUser();

  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const hasInsightsTool = user?.featureFlags?.includes(UserFeatureFlag.INSIGHTS_TOOL);
  const hasThreadsTool = user?.featureFlags?.includes(UserFeatureFlag.THREADS_TOOL);
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  const shouldRedirectToAdmin =
    !isAdminRoute &&
    shouldRedirectAuthenticatedUserToAdmin({
      impersonation: user?.impersonation,
      role: user?.profile?.role,
    });

  const router = createBrowserRouter(
    [{ element: <Navigate to={routes.home.path} />, path: '*' }].concat(
      Object.values(routes).map(({ element, path }) => ({
        element: (
          <>
            <AutomaticModalsComponent />
            {element()}
            {hasInsightsTool && <InsightsIframeManager />}
            {hasThreadsTool && <ThreadsIframeManager />}
            <ScrollRestoration />
          </>
        ),
        path,
      })),
    ),
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteToken = urlParams.get('inviteToken');
    const referrerCode = urlParams.get('referrerCode');

    if (inviteToken) setInviteToken(inviteToken);
    if (referrerCode) setReferrerCode(referrerCode);

    if (inviteToken || referrerCode) {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.getCurrentUser });
    }
  }, [setInviteToken, setReferrerCode]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (!isAuthenticated) {
    clearAppCache();
    loginWithRedirect({
      appState: {
        returnTo: hasLocalSession
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : routes.home.path,
      },
    });
    return <LoadingState />;
  }

  if (isUserPending) {
    return <LoadingState />;
  }

  if (shouldRedirectToAdmin) {
    window.location.replace(routes.adminUsers.path);
    return <LoadingState />;
  }

  return (
    <NuqsAdapter>
      <>
        <ImpersonationBanner />
        <Toaster />
        <div className="relative">
          <RouterProvider router={router} />
        </div>
      </>
    </NuqsAdapter>
  );
};
